import transformMagazineMagnoliaCategory from '@/helpers/transformMagazineMagnoliaCategory';
import { animated, useSpring } from '@react-spring/web';
import Background from '@uikit/components/Background/Background';
import MagazineHeroArea from '@uikit/composites/HeroArea/MagazineHeroArea';
import clsx from 'clsx';
import React, { useState } from 'react';
import Button from '../../Button/Button';
import {
  ButtonColor,
  ButtonSize,
  ShowAddon,
  ShowIcon,
} from '../../Button/consts';
import { ClickableAction } from '../../Clickable/consts';
import Icon from '../../Icon/Icon';
import MagazineShadow from '../MagazineShadow/MagazineShadow';
import MagazineStageBox from '../MagazineStageBox/MagazineStageBox';
import styles from './MagazineHeroCarousel.module.scss';
import {
  MagazineHeroCarouselItem,
  MagazineHeroCarouselItemProps,
  MagazineHeroCarouselProps,
} from './interfaces';

const MagazineHeroCarousel = ({
  children,
  contents,
}: MagazineHeroCarouselProps) => {
  let items: MagazineHeroCarouselItemProps[] = [];

  if (contents) {
    items =
      contents?.[0].contents.map(({ props }) => ({
        ...props,
      })) ?? [];
  } else if (children) {
    items = (
      React.Children.map(children, (child) => {
        if (React.isValidElement<{ item: MagazineHeroCarouselItem }>(child)) {
          return { ...child.props.item.props };
        }

        return undefined;
      }) ?? []
    ).filter((item) => typeof item !== 'undefined');
  }

  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentImageUrl, setCurrentImageUrl] = useState(items[0]?.image);
  const [currentVimeoUrl, setCurrentVimeoUrl] = useState(items[0]?.vimeoUrl);
  const [currentCaption, setCurrentCaption] = useState(items[0]?.caption);
  const [currentButtonIcon, setCurrentButtonIcon] = useState(
    items[0]?.buttonIcon
  );
  const [currentButtonText, setCurrentButtonText] = useState(
    items[0]?.buttonText
  );
  const [currentButtonUrl, setCurrentButtonUrl] = useState(items[0]?.link);

  const [isCaptionVisible, setIsCaptionVisible] = useState(true);

  const animationProps = useSpring({
    opacity: isCaptionVisible ? 1 : 0,
  });

  if (items.length === 0) {
    return null;
  }

  const updateContent = (idx: number) => {
    setCurrentImageUrl(items[idx].image);
    setCurrentVimeoUrl(items[idx].vimeoUrl);
    setCurrentCaption(items[idx].caption);
    setCurrentButtonIcon(items[idx].buttonIcon);
    setCurrentButtonText(items[idx].buttonText);
    setCurrentButtonUrl(items[idx].link);
  };

  const decreaseIndex = () => {
    if (currentIndex > 0) {
      updateIndex(currentIndex - 1);
    } else {
      updateIndex(items.length - 1);
    }
  };

  const increaseIndex = () => {
    if (currentIndex < items.length - 1) {
      updateIndex(currentIndex + 1);
    } else {
      updateIndex(0);
    }
  };
  const updateIndex = (idx: number) => {
    setCurrentIndex(idx);
    setIsCaptionVisible(false);
    setTimeout(() => {
      updateContent(idx);
    }, 250);
    setTimeout(() => {
      setIsCaptionVisible(true);
    }, 300);
  };

  return (
    <>
      <MagazineHeroArea>
        <animated.div style={animationProps}>
          <Background.Picture asset={currentImageUrl} />
          {currentVimeoUrl && <Background.VimeoVideo src={currentVimeoUrl} />}
          <MagazineShadow type={3} />
        </animated.div>
      </MagazineHeroArea>
      <MagazineStageBox
        category={transformMagazineMagnoliaCategory(
          items[currentIndex]?.category
        )}
      >
        {/* TODO lots of duplicate code here */}
        {items.length > 1 && (
          <div className={styles.slideNav}>
            <div
              className={styles.slideNavButton}
              onClick={() => decreaseIndex()}
            >
              <Icon variant={'action/chevron-left'} size="iconSize24" />
            </div>
            <div>
              <div
                className={clsx(styles.slideNavButton, styles.slideNavBullet, {
                  [styles.active]: currentIndex === 0,
                })}
                onClick={() => updateIndex(0)}
              >
                1
              </div>
              <div
                className={clsx(styles.slideNavButton, styles.slideNavBullet, {
                  [styles.active]: currentIndex === 1,
                })}
                onClick={() => updateIndex(1)}
              >
                2
              </div>
              {items.length > 2 && (
                <div
                  className={clsx(
                    styles.slideNavButton,
                    styles.slideNavBullet,
                    { [styles.active]: currentIndex === 2 }
                  )}
                  onClick={() => updateIndex(2)}
                >
                  3
                </div>
              )}
            </div>
            <div
              className={styles.slideNavButton}
              onClick={() => increaseIndex()}
            >
              <Icon variant={'action/chevron-right'} size="iconSize24" />
            </div>
          </div>
        )}{' '}
        <animated.div style={animationProps}>
          <h2 className={styles.title}>{currentCaption}</h2>
          <Button
            color={ButtonColor.Secondary}
            showAddon={ShowAddon.None}
            className={styles.button}
            showIcon={currentButtonIcon ? ShowIcon.ShowIcon : ShowIcon.None}
            actionType={ClickableAction.Linkintern}
            linkintern={currentButtonUrl}
            icon={currentButtonIcon}
            size={ButtonSize.Large}
          >
            {currentButtonText}
          </Button>
        </animated.div>
      </MagazineStageBox>
    </>
  );
};

export default MagazineHeroCarousel;
